import { useQuery } from "@apollo/client";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { keyBy, partition } from "lodash";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { ReactNode, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Create10DlcBrandAndCampaignRegistrationMutation,
  Create10DlcBrandAndCampaignRegistrationMutationVariables,
  EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutation,
  EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutationVariables,
  GetTenDlcCampaignsForSubaccountQuery,
  ListOwnedPhoneNumbersQuery,
  ProviderName,
  PurchaseTenDlcPhoneNumbersMutation,
  RetoolTenDlcRegistrationsQuery,
} from "~frontend/__generated__/graphql";
import { CollapsibleContainer } from "~frontend/components/CollapsibleContainer";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import ChevronUp from "~frontend/components/icons/ChevronUp";
import {
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_10DLC,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_SINCH_10DLC,
  GET_SUBACCOUNT_ADMIN_FIELDS,
} from "~frontend/graphql/subaccounts";
import {
  LIST_10DLC_CAMPAIGNS,
  PURCHASE_10DLC_NUMBERS,
  RETOOL_CAMPAIGN_REGISTRATIONS,
} from "~frontend/graphql/tenDlcCampaigns";
import {
  CREATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION,
  GET_REGISTRATION,
} from "~frontend/graphql/tenDlcRegistrations";
import {
  useCurrentUserIsStaff,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { PHONE_NUMBER_REFETCH_QUERIES } from ".";
import { ErroredTenDlcCampaigns } from "./ErroredTenDlcCampaigns";
import { PendingTenDlcCampaigns } from "./PendingTenDlcCampaigns";
import { PhoneNumberTable } from "./PhoneNumberTable";
import { Purchase10DlcPhoneNumberDialog } from "./Purchase10DlcPhoneNumberDialog";
import { InProgressRegistrations } from "./TenDlc/InProgressRegistrations";
import { TenDlcCampaign } from "./TenDlcCampaign";
import { OwnedPhoneNumberForList } from "./types";

export const BorderBoxStyles = {
  borderRadius: 0.5,
  border: `1px solid ${palette.lightGray}`,
  p: 3,
  pb: 2,
  width: "100%",
};

export const TenDlc = ({
  numbers,
  sort,
  providerFilter,
}: {
  numbers: ListOwnedPhoneNumbersQuery["listOwnedPhoneNumbers"];
  sort: string | null;
  providerFilter: string[] | null;
}): ReactNode => {
  const canUseBuiltIn10DlcForm = useFeatureFlag("tenDlcRegistrationInApp");
  const { subaccount, subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();

  const isStaff = useCurrentUserIsStaff();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [importedSectionCollapsed, setImportedSectionCollapsed] =
    useState(false);
  const subaccountApolloClient = useNonCacheClearingApolloClient();

  const [purchasePhoneNumbers] =
    useMutation<PurchaseTenDlcPhoneNumbersMutation>(PURCHASE_10DLC_NUMBERS, {
      refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
      awaitRefetchQueries: true,
    });

  const handlePurchase = async (values: {
    quantity: number;
    tcrCampaignId: string;
    areaCodes: string[];
  }): Promise<
    PurchaseTenDlcPhoneNumbersMutation["purchaseTenDlcPhoneNumbers"]
  > => {
    const { data, errors } = await purchasePhoneNumbers({
      variables: {
        input: {
          subaccountId: subaccount.id,
          tcrCampaignId: values.tcrCampaignId,
          quantity: values.quantity,
          areaCodes: values.areaCodes,
        },
      },
    });
    if (errors) {
      throw errors;
    }
    setPurchaseDialogOpen(false);

    return data!.purchaseTenDlcPhoneNumbers;
  };

  // if an error occurs while loading retool campaigns, silently ignore
  const { data: retoolData, loading: retoolDataLoading } =
    useQuery<RetoolTenDlcRegistrationsQuery>(RETOOL_CAMPAIGN_REGISTRATIONS, {
      variables: {
        input: {
          subaccountId: subaccount.id,
        },
      },
      skip: !subaccount,
    });

  const {
    data: tenDlcCampaigns,
    loading: tenDlcCampaignsLoading,
    error: tenDlcCampaignsError,
  } = useQuery<GetTenDlcCampaignsForSubaccountQuery>(LIST_10DLC_CAMPAIGNS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
    skip: !subaccount,
  });
  const campaigns = tenDlcCampaigns?.tenDlcCampaignsForSubaccount || [];

  const [localCampaigns, erroredLocalCampaigns] = partition(
    _.sortBy(campaigns || [], c => [c.provider, c.tcrCampaignId]),
    campaign => !campaign.error
  );

  const localCampaignsByRetoolId = keyBy(
    localCampaigns.filter(campaign => campaign.retoolId),
    "retoolId"
  );

  // Campaigns that exist in Retool but have not yet been synced to STW-T
  const pendingCampaigns = (retoolData?.retoolTenDlcRegistrations || []).filter(
    campaign => campaign.id && !localCampaignsByRetoolId[campaign.id]
  );

  // There may be overlap between Pending campaigns and Errored campaigns;
  // we will only show errored campaigns to staff. If an admin is logged in,
  // we don't want to alarm them with a missing Campaign Pending blurb.
  const erroredRetoolCampaigns = (
    retoolData?.retoolTenDlcRegistrations || []
  ).filter(
    campaign =>
      campaign.error &&
      campaign.id &&
      !erroredLocalCampaigns.find(c => c.retoolId === campaign.id) // to avoid duplicates
  );

  const campaignsCanPurchase = localCampaigns.filter(
    campaign =>
      campaign.status === "ACTIVE" &&
      campaign.currentNumberCount !== null &&
      campaign.currentNumberCount !== undefined &&
      campaign.currentNumberCount < campaign.numberLimit &&
      [ProviderName.Bandwidth, ProviderName.Sinch].includes(campaign.provider)
  );
  // Only allow purchase of numbers if there are active campaigns that have not
  // reached their number limit
  const showPurchaseButton = campaignsCanPurchase.length > 0;

  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [createRegistration] = useMutation<
    Create10DlcBrandAndCampaignRegistrationMutation,
    Create10DlcBrandAndCampaignRegistrationMutationVariables
  >(CREATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION, {
    onErrorSnackbarMessage: "Error creating registration",
    refetchQueries: [GET_REGISTRATION],
    awaitRefetchQueries: true,
  });

  const handleRegisterButtonClick = async () => {
    if (canUseBuiltIn10DlcForm) {
      setCreatingCampaign(true);
      const { data, errors } = await createRegistration({
        variables: {
          input: {
            subaccountId: subaccount.id,
          },
        },
      });

      if (errors || !data?.createBrandAndCampaignRegistration) {
        enqueueSnackbar("Error starting registration", { variant: "error" });
        setCreatingCampaign(false);
      } else {
        await navigate(
          subaccountLink(
            `/10dlc/registration/${data.createBrandAndCampaignRegistration.campaignRegistrations[0].id}`
          )
        );
      }
    } else {
      await navigate(subaccountLink("/10dlc/registration"));
    }
  };

  const registerForDlcButton = (
    <Button
      variant="contained"
      color="primary"
      sx={{ fontSize: 12 }}
      loading={creatingCampaign}
      disabled={creatingCampaign}
      onClick={handleRegisterButtonClick}
    >
      Register for 10DLC
    </Button>
  );

  // This variable includes:
  // - numbers that do not have a 10DLC Campaign ID
  // - numbers that have a 10DLC Campaign ID, but the campaign does not
  //   exist in the list of campaigns in our database
  const tenDlcNumbersWithoutCampaigns = numbers.filter(
    number =>
      !number.tenDlcCampaignId ||
      !tenDlcCampaigns?.tenDlcCampaignsForSubaccount.find(
        campaign => campaign.tcrCampaignId === number.tenDlcCampaignId
      )
  );

  const importedNumbersPresent = tenDlcNumbersWithoutCampaigns.length;

  const showEmptySection =
    pendingCampaigns.length === 0 &&
    localCampaigns.length === 0 &&
    !importedNumbersPresent;

  const showRegisterButtonInImportsHeader =
    pendingCampaigns.length === 0 &&
    localCampaigns.length === 0 &&
    importedNumbersPresent;

  const [setDefaultMessagingStrategyBandwidth10Dlc] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutation,
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_10DLC, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategySinch10Dlc] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutation,
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_SINCH_10DLC, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategyFake10Dlc] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutation,
    EditSubaccountDefaultMessagingStrategyBandwidth10DlcMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_10DLC, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const setDefaultPhoneNumber = useCallback(
    async (phoneNumber: OwnedPhoneNumberForList) => {
      if (phoneNumber.provider === ProviderName.Bandwidth) {
        await setDefaultMessagingStrategyBandwidth10Dlc({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (phoneNumber.provider === ProviderName.Sinch) {
        await setDefaultMessagingStrategySinch10Dlc({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (phoneNumber.provider === ProviderName.Fake) {
        await setDefaultMessagingStrategyFake10Dlc({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else {
        throw new Error(`Unsupported provider: ${phoneNumber.provider}`);
      }
    },
    [
      subaccount,
      setDefaultMessagingStrategyBandwidth10Dlc,
      setDefaultMessagingStrategySinch10Dlc,
      setDefaultMessagingStrategyFake10Dlc,
    ]
  );

  if (tenDlcCampaignsLoading || retoolDataLoading) {
    return <Loading />;
  }

  if (tenDlcCampaignsError) {
    return <ErrorCard errors={[tenDlcCampaignsError]} />;
  }

  return (
    <CollapsibleContainer
      header="10DLC Numbers"
      headerActions={
        !showRegisterButtonInImportsHeader || showEmptySection
          ? [
              registerForDlcButton,
              showPurchaseButton ? (
                <Button
                  variant="contained"
                  sx={{ fontSize: 12 }}
                  onClick={async () => setPurchaseDialogOpen(true)}
                >
                  + Add 10DLC Numbers
                </Button>
              ) : null,
            ]
          : []
      }
    >
      <Purchase10DlcPhoneNumberDialog
        open={purchaseDialogOpen}
        onClose={() => setPurchaseDialogOpen(false)}
        onSubmit={handlePurchase}
        availableCampaigns={campaignsCanPurchase}
      />

      <InProgressRegistrations />

      {isStaff ? (
        <ErroredTenDlcCampaigns
          campaigns={[...erroredLocalCampaigns, ...erroredRetoolCampaigns]}
        />
      ) : null}

      {showEmptySection ? (
        <PhoneNumberTable
          rows={[]}
          sort={sort}
          providerFilter={providerFilter}
        />
      ) : (
        <>
          <PendingTenDlcCampaigns campaigns={pendingCampaigns} />

          {localCampaigns.map((campaign, idx) => (
            <Box
              data-testid={`campaign-${campaign.tcrCampaignId}`}
              key={campaign.tcrCampaignId}
              mt={idx > 0 ? 6 : 3}
              sx={BorderBoxStyles}
            >
              <TenDlcCampaign
                campaign={campaign}
                tableParams={{
                  sort,
                  providerFilter,
                  setDefaultPhoneNumber,
                }}
              />
            </Box>
          ))}
        </>
      )}

      {importedNumbersPresent ? (
        <Box
          data-testid="imported-tendlc-numbers"
          sx={{ ...BorderBoxStyles, pb: 3, mt: 3 }}
        >
          <FlexBox justifyContent="space-between" alignItems="center">
            <Typography variant="h2Bold" component="div">
              Imported 10DLC Numbers
            </Typography>

            <FlexBox gap={1.25}>
              {showRegisterButtonInImportsHeader ? registerForDlcButton : null}
              <IconButton
                onClick={() =>
                  setImportedSectionCollapsed(!importedSectionCollapsed)
                }
              >
                {importedSectionCollapsed ? (
                  <ChevronDown sx={{ fontSize: "12px", strokeWidth: 2 }} />
                ) : (
                  <ChevronUp sx={{ fontSize: "12px", strokeWidth: 2 }} />
                )}
              </IconButton>
            </FlexBox>
          </FlexBox>

          {importedSectionCollapsed ? null : (
            <PhoneNumberTable
              rows={tenDlcNumbersWithoutCampaigns}
              providerFilter={providerFilter}
              sort={sort}
              setDefaultPhoneNumber={setDefaultPhoneNumber}
              showCampaignId
            />
          )}
        </Box>
      ) : null}
    </CollapsibleContainer>
  );
};
